import {
  ScoreGaugeView,
  useDefaultScoreGaugeStyle,
} from '@euler/app/components/score/ScoreGauge';
import { calculateScoreViewDimension } from '@euler/app/components/score/util';
import { Typography } from '@euler/app/components/typography';
import {
  InspectionResultCellType,
  useInspectionReportModels,
} from '@euler/app/flows/report/functions';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { Colors } from '@euler/components/Colors';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { VehicleReport } from '@euler/model/report';
import { formatDateTime } from '@euler/utils/formatters';
import { MotiView } from 'moti';
import { ReactNode, memo, useCallback, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  Platform,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { Fill } from 'react-slot-fill';
import { DefectiveStatsView } from './DefectiveStatsView';
import { InspectionResultsView } from './InspectionResultsView';

const kCellMargin = 20;

export const InspectionReportViewForIpad = memo(
  ({
    report,
    flowId,
    productManifest,
    faultLikeSubject,
  }: {
    report: VehicleReport;
    flowId?: string;
    productManifest?: ProductManifestProps;
    faultLikeSubject?: FaultInspectionItem;
  }) => {
    const { rpx } = useResponsiveContext();

    const [layout, setLayout] = useState<LayoutRectangle>();

    const onLayout = useCallback((e: LayoutChangeEvent) => {
      setLayout(e.nativeEvent.layout);
    }, []);

    const flow = report.inspection?.flows?.find(x => x.id === flowId);
    const technicianName =
      flow?.technicianName ??
      report.inspection?.technicianName ??
      report.inspection?.flows?.[0].technicianName ??
      '无';
    const inspectedAt = flow?.finishedAt ?? flow?.createdAt ?? new Date();

    return (
      <>
        <View
          css={`
            flex: 1;
            justify-content: center;
          `}
          onLayout={onLayout}
        >
          {layout != null && (
            <Content
              report={report}
              width={layout.width}
              height={Math.floor(layout.height * 0.75)}
              productManifest={productManifest}
              faultLikeSubject={faultLikeSubject}
              flowId={flowId}
              css={`
                flex: 1;
              `}
            />
          )}
        </View>
        <Fill name="reports/inspection/page-container/footer/left">
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            检测技师: {technicianName}
          </Typography.Label>
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            检测时间: {formatDateTime(inspectedAt)}
          </Typography.Label>
        </Fill>
      </>
    );
  },
);

const Content = memo(
  ({
    width,
    height,
    report,
    style,
    flowId,
    productManifest,
    faultLikeSubject,
  }: {
    width: number;
    height: number;
    report: VehicleReport;
    style?: StyleProp<ViewStyle>;
    flowId?: string;
    productManifest?: ProductManifestProps;
    faultLikeSubject?: FaultInspectionItem;
  }) => {
    const { rpx } = useResponsiveContext();
    const isLandscape = useIsLandscape();
    const { cells } = useInspectionReportModels(report, {
      inspectionResultsOnly: true,
      flowId,
      faultLikeSubject,
    });
    const cellWidth = (width - 6 * rpx(kCellMargin)) / 10;
    const cellHeight = height - 2 * rpx(kCellMargin);
    const leftFlex = isLandscape ? 5 : 4;
    const groupHeaderStyle = useCallback(
      (
        group: InspectionResultCellType<'group-header'>,
      ): StyleProp<ViewStyle> => {
        return group.index === 0 ? { marginTop: 0 } : undefined;
      },
      [],
    );
    return (
      <HStack justifyContent="space-between" alignItems="stretch" style={style}>
        <Cell
          index={0}
          css={`
            flex: ${leftFlex};
            padding: 0;
            margin-left: 0;
          `}
        >
          <ScoreView
            width={cellWidth * leftFlex}
            height={cellHeight}
            report={report}
          />
        </Cell>
        <Cell
          index={1}
          css={`
            flex: ${10 - leftFlex};
            box-shadow: 0 0 ${rpx(15)}px rgba(0, 0, 0, 0.15);
          `}
          contentStyle={{
            backgroundColor: '#fff',
          }}
        >
          <LayoutProviderView
            css={`
              flex: 1;
            `}
          >
            <InspectionResultsView
              cells={cells}
              groupHeaderStyle={groupHeaderStyle}
              showsVerticalScrollIndicator={Platform.OS === 'web'}
              productManifest={productManifest}
              faultLikeSubject={faultLikeSubject}
            />
          </LayoutProviderView>
        </Cell>
      </HStack>
    );
  },
);

const Cell = memo(
  ({
    index,
    style,
    contentStyle,
    children,
  }: {
    index: number;
    style?: StyleProp<ViewStyle>;
    contentStyle?: StyleProp<ViewStyle>;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <MotiView
        from={{ opacity: 0, translateX: rpx(250) }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ type: 'timing', duration: 500 }}
        delay={index * 100}
        css={`
          margin-left: ${rpx(kCellMargin)}px;
          margin-right: ${rpx(kCellMargin)}px;
        `}
        style={style}
      >
        <View
          css={`
            flex: 1;
            overflow: hidden;
            border-radius: ${rpx(32)}px;
          `}
          style={contentStyle}
        >
          {children}
        </View>
      </MotiView>
    );
  },
);

const ScoreView = memo(
  (props: {
    report: VehicleReport;
    width: number;
    height: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { report } = props;
    const dimens = calculateScoreViewDimension(props.width, props.height);
    const width = Math.min(460, Math.round(dimens.width));
    const height =
      Math.round(dimens.height) * (width / Math.round(dimens.width));
    const isLandscape = useIsLandscape();
    const statItemMargin = isLandscape ? 4 : 10;
    const defaultTheme = useDefaultScoreGaugeStyle();
    const theme = useMemo(() => {
      return {
        ...defaultTheme,
        scoreLabelColor: 'rgba(255, 255, 255, 0.75)',
        tickLabelColor: 'rgba(255, 255, 255, 0.5)',
        tickLabelFontSize: 14,
        scoreLabelFontSize: isLandscape ? 24 : 16,
        scoreFontSize: isLandscape ? 72 : 64,
      };
    }, [defaultTheme, isLandscape]);

    const statItemWidth =
      (width - 30 - statItemMargin * 4) / (isLandscape ? 4 : 2);
    return (
      <View
        css={`
          padding: 15px;
          margin-top: 10px;
          align-items: center;
          justify-content: center;
          flex: 1;
        `}
      >
        <ScoreGaugeView
          score={report.inspection?.score ?? report.score ?? 100}
          width={width}
          height={height * (Math.min(width, 460) / width)}
          theme={theme}
          scoreTextContainerStyle={{
            transform: [{ translateY: 10 }],
          }}
        />
        <DefectiveStatsView
          report={report.inspection ?? undefined}
          size={statItemWidth - 20}
          strokeWidth={isLandscape ? 4 : 6}
          itemStyle={{
            width: statItemWidth,
            margin: statItemMargin,
          }}
          labelContainerStyle={{
            marginTop: 4,
          }}
          labelStyle={{
            fontSize: isLandscape ? 14 : 16,
            lineHeight: 20,
          }}
          iconSize={24}
          css={`
            justify-content: center;
            margin-top: ${isLandscape ? 20 : 44}px;
            flex-wrap: ${isLandscape ? 'nowrap' : 'wrap'};
          `}
        />
      </View>
    );
  },
);
